<template>
  <div class="history-modal-wrapper">
    <!-- Trigger Button -->
    <button
      v-if="!hideButton"
      type="button"
      class="btn btn-sm btn-outline-info"
      :class="buttonClass"
      @click="openModal"
      :title="buttonTitle"
    >
      <i class="fas fa-history"></i>
      <span v-if="showButtonText" class="ml-1">History</span>
    </button>

    <!-- Modal -->
    <div
      v-if="showModal"
      class="modal fade show d-block"
      tabindex="-1"
      role="dialog"
      aria-labelledby="historyModalLabel"
      style="background: rgba(0, 0, 0, 0.8);"
      @click.self="closeModal"
    >
      <div class="modal-dialog modal-lg history-modal-dialog" role="document">
        <div class="modal-content bg-dark text-light">
          <!-- Header -->
          <div class="modal-header border-bottom border-secondary">
            <h5 class="modal-title" id="historyModalLabel">
              <i class="fas fa-history mr-2"></i>
              Parameter History
              <span v-if="parameterName" class="parameter-name-badge ml-2">{{ parameterName }}</span>
            </h5>
            <button type="button" class="close text-light" @click="closeModal">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <!-- Body -->
          <div class="modal-body scrollable-body">
            <!-- Loading State -->
            <div v-if="loading" class="loading-state">
              <i class="fas fa-spinner fa-spin fa-2x"></i>
              <p class="mt-2">Loading history...</p>
            </div>

            <!-- Error State -->
            <div v-else-if="error" class="error-state">
              <i class="fas fa-exclamation-triangle fa-2x text-warning"></i>
              <p class="mt-2">{{ error }}</p>
              <button class="btn btn-sm btn-outline-light" @click="fetchHistory">
                Try Again
              </button>
            </div>

            <!-- Empty State -->
            <div v-else-if="!history || history.length === 0" class="empty-state">
              <i class="fas fa-inbox fa-3x text-muted"></i>
              <p class="mt-3 text-muted">No history records found</p>
              <small class="text-muted">Changes will appear here after the parameter is edited.</small>
            </div>

            <!-- History Timeline -->
            <div v-else class="history-timeline">
              <div
                v-for="(entry, index) in history"
                :key="index"
                class="history-entry"
              >
                <!-- Entry Header (Commit Card) -->
                <div class="entry-header" @click="toggleEntry(index)">
                  <div class="entry-indicator">
                    <div class="timeline-dot"></div>
                    <div v-if="index < history.length - 1" class="timeline-line"></div>
                  </div>
                  <div class="entry-content">
                    <div class="entry-meta">
                      <span class="entry-user">
                        <i class="fas fa-user-circle mr-1"></i>
                        {{ entry.user || 'Unknown User' }}
                      </span>
                      <span class="entry-date">
                        <i class="fas fa-clock mr-1"></i>
                        {{ formatDate(entry.timestamp) }}
                      </span>
                    </div>
                    <div class="entry-summary">
                      <span class="changes-count">
                        {{ entry.changes ? entry.changes.length : 0 }} change{{ entry.changes && entry.changes.length !== 1 ? 's' : '' }}
                      </span>
                      <span class="entry-labels">
                        <span
                          v-for="(change, changeIdx) in getChangeSummary(entry.changes)"
                          :key="changeIdx"
                          class="change-label"
                          :class="getChangeLabelClass(change.label)"
                        >
                          {{ truncateLabel(change.label) }}
                        </span>
                      </span>
                    </div>
                    <i
                      class="fas expand-icon"
                      :class="expandedEntries.includes(index) ? 'fa-chevron-up' : 'fa-chevron-down'"
                    ></i>
                  </div>
                </div>

                <!-- Expanded Changes -->
                <transition name="slide">
                  <div v-if="expandedEntries.includes(index)" class="entry-changes">
                    <div
                      v-for="(change, changeIdx) in entry.changes"
                      :key="changeIdx"
                      class="change-item"
                    >
                      <div class="change-header" @click="toggleChange(index, changeIdx)">
                        <span class="change-type-badge" :class="getTypeBadgeClass(change.type)">
                          {{ change.type }}
                        </span>
                        <span class="change-label-text">{{ change.label }}</span>
                        <i
                          class="fas expand-icon-small"
                          :class="isChangeExpanded(index, changeIdx) ? 'fa-minus' : 'fa-plus'"
                        ></i>
                      </div>
                      <transition name="fade">
                        <div v-if="isChangeExpanded(index, changeIdx)" class="change-diff">
                          <DiffViewer
                            :old-value="change.old_value"
                            :new-value="change.new_value"
                            :type="change.type"
                          />
                        </div>
                      </transition>
                    </div>
                  </div>
                </transition>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer border-top border-secondary">
            <div class="footer-info">
              <small v-if="history && history.length > 0" class="text-muted">
                Showing last {{ history.length }} change{{ history.length !== 1 ? 's' : '' }} (max 10)
              </small>
            </div>
            <button type="button" class="btn btn-secondary" @click="closeModal">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { managerService } from '../../services/manager.service';
import DiffViewer from './DiffViewer.vue';
import * as Diff from 'diff';

export default {
  name: 'HistoryModal',
  components: {
    DiffViewer
  },
  props: {
    parameterId: {
      type: String,
      required: true
    },
    parameterName: {
      type: String,
      default: ''
    },
    currentParameter: {
      type: Object,
      default: () => ({})
    },
    hideButton: {
      type: Boolean,
      default: false
    },
    showButtonText: {
      type: Boolean,
      default: true
    },
    buttonClass: {
      type: String,
      default: ''
    },
    buttonTitle: {
      type: String,
      default: 'View History'
    }
  },
  data() {
    return {
      showModal: false,
      loading: false,
      error: null,
      history: [],
      expandedEntries: [],
      expandedChanges: {} // { entryIndex: [changeIdx1, changeIdx2] }
    };
  },
  computed: {
    appId() {
      return this.$store.getters.activeApp;
    }
  },
  methods: {
    async openModal() {
      this.showModal = true;
      this.expandedEntries = [];
      this.expandedChanges = {};
      await this.fetchHistory();
    },
    closeModal() {
      this.showModal = false;
      this.$emit('closed');
    },
    
    // Convert value to string for display
    valueToString(value) {
      if (value === null || value === undefined) return '';
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          if (typeof parsed === 'object' && parsed !== null) {
            return JSON.stringify(parsed, null, 2);
          }
        } catch (e) {}
        return value;
      }
      if (typeof value === 'object') {
        return JSON.stringify(value, null, 2);
      }
      return String(value);
    },
    
    // Get current value from parameter based on property path
    getCurrentValue(property) {
      if (!this.currentParameter || !property) return '';
      
      if (property === 'default_value') {
        return this.valueToString(this.currentParameter.default_value);
      }
      if (property === 'desc') {
        return this.currentParameter.desc || '';
      }
      if (property === 'tag') {
        return this.currentParameter.tag || '';
      }
      if (property === 'os') {
        return this.valueToString(this.currentParameter.os);
      }
      if (property === 'is_hidden') {
        return String(this.currentParameter.is_hidden || false);
      }
      if (property === 'is_active') {
        return String(this.currentParameter.is_active !== false);
      }
      if (property === 'created') {
        return '';
      }
      
      // Handle nested properties like audience_values.xxx, priorities.xxx, categories.xxx
      const parts = property.split('.');
      if (parts.length === 2) {
        const [parent, key] = parts;
        const parentObj = this.currentParameter[parent] || {};
        return this.valueToString(parentObj[key]);
      }
      
      return '';
    },
    
    // Reconstruct old/new values from patches using the "Time Machine" approach
    reconstructHistory(rawHistory) {
      if (!rawHistory || rawHistory.length === 0) return [];
      
      // Build working state from current parameter values
      const workingState = {};
      
      // Process history from newest to oldest, reconstructing values
      const processedHistory = rawHistory.map((entry, entryIndex) => {
        const processedChanges = (entry.changes || []).map(change => {
          const property = change.property;
          
          // Initialize working state for this property if not exists
          if (workingState[property] === undefined) {
            workingState[property] = this.getCurrentValue(property);
          }
          
          // The "new value" at this point in time is what's in working state
          const newValue = workingState[property];
          
          // Apply reverse patch to get "old value"
          let oldValue = newValue;
          if (change.patch) {
            try {
              const result = Diff.applyPatch(newValue, change.patch);
              if (result !== false) {
                oldValue = result;
              }
            } catch (e) {
              console.warn('Failed to apply patch for', property, e);
            }
          }
          
          // Update working state to old value for next (older) entry
          workingState[property] = oldValue;
          
          return {
            ...change,
            old_value: oldValue,
            new_value: newValue
          };
        });
        
        return {
          ...entry,
          changes: processedChanges
        };
      });
      
      return processedHistory;
    },
    
    async fetchHistory() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await managerService.getParameterHistory({
          aid: this.appId,
          parameterId: this.parameterId
        });
        
        if (response && response.data && response.data.status === 200) {
          const rawHistory = response.data.result || [];
          // Reconstruct old/new values from patches
          this.history = this.reconstructHistory(rawHistory);
          // Auto-expand first entry if there's history
          if (this.history.length > 0) {
            this.expandedEntries = [0];
          }
        } else {
          this.error = 'Failed to load history';
        }
      } catch (err) {
        console.error('Error fetching parameter history:', err);
        this.error = 'An error occurred while loading history';
      } finally {
        this.loading = false;
      }
    },
    toggleEntry(index) {
      const idx = this.expandedEntries.indexOf(index);
      if (idx > -1) {
        this.expandedEntries.splice(idx, 1);
      } else {
        this.expandedEntries.push(index);
      }
    },
    toggleChange(entryIndex, changeIndex) {
      if (!this.expandedChanges[entryIndex]) {
        this.$set(this.expandedChanges, entryIndex, []);
      }
      
      const changes = this.expandedChanges[entryIndex];
      const idx = changes.indexOf(changeIndex);
      
      if (idx > -1) {
        changes.splice(idx, 1);
      } else {
        changes.push(changeIndex);
      }
    },
    isChangeExpanded(entryIndex, changeIndex) {
      return this.expandedChanges[entryIndex] && 
             this.expandedChanges[entryIndex].includes(changeIndex);
    },
    formatDate(timestamp) {
      if (!timestamp) return 'Unknown';
      
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      // Relative time for recent changes
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
      
      // Full date for older changes
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    getChangeSummary(changes) {
      if (!changes || changes.length === 0) return [];
      // Return first 3 changes for summary
      return changes.slice(0, 3);
    },
    truncateLabel(label) {
      if (!label) return '';
      if (label.length > 25) return label.substring(0, 22) + '...';
      return label;
    },
    getChangeLabelClass(label) {
      if (!label) return '';
      const lowerLabel = label.toLowerCase();
      if (lowerLabel.includes('created')) return 'label-created';
      if (lowerLabel.includes('removed') || lowerLabel.includes('deleted')) return 'label-removed';
      if (lowerLabel.includes('added')) return 'label-added';
      if (lowerLabel.includes('active status')) return 'label-status';
      if (lowerLabel.includes('priority')) return 'label-priority';
      if (lowerLabel.includes('audience')) return 'label-audience';
      return 'label-default';
    },
    getTypeBadgeClass(type) {
      switch (type) {
        case 'json': return 'badge-json';
        case 'string': return 'badge-string';
        case 'number': return 'badge-number';
        case 'boolean': return 'badge-boolean';
        case 'addition': return 'badge-addition';
        case 'deletion': return 'badge-deletion';
        default: return 'badge-default';
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "./styles/modal_elements.scss";
</style>

<style scoped>
/* Modal sizing */
.history-modal-dialog {
  max-width: 800px;
  width: 90%;
}

.scrollable-body {
  max-height: calc(100vh - 220px);
  overflow-y: auto;
  padding: 1rem;
}

/* Parameter name badge */
.parameter-name-badge {
  background-color: rgba(99, 179, 237, 0.2);
  color: #63b3ed;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 14px;
  font-weight: normal;
}

/* Loading, Error, Empty states */
.loading-state,
.error-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  text-align: center;
}

/* Timeline styles */
.history-timeline {
  position: relative;
}

.history-entry {
  position: relative;
  margin-bottom: 0;
}

.entry-header {
  display: flex;
  cursor: pointer;
  padding: 0.75rem 0;
  transition: background-color 0.2s;
  border-radius: 6px;
}

.entry-header:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.entry-indicator {
  position: relative;
  width: 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.timeline-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #238636;
  border: 2px solid #30363d;
  z-index: 1;
}

.timeline-line {
  position: absolute;
  top: 20px;
  width: 2px;
  height: calc(100% + 10px);
  background-color: #30363d;
}

.entry-content {
  flex: 1;
  min-width: 0;
  padding-left: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.entry-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  font-size: 13px;
}

.entry-user {
  color: #58a6ff;
  font-weight: 500;
}

.entry-date {
  color: #8b949e;
}

.entry-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

.changes-count {
  color: #8b949e;
  font-size: 12px;
}

.entry-labels {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.change-label {
  font-size: 11px;
  padding: 0.15rem 0.4rem;
  border-radius: 3px;
  background-color: #30363d;
  color: #c9d1d9;
}

.change-label.label-created {
  background-color: rgba(35, 134, 54, 0.2);
  color: #3fb950;
}

.change-label.label-removed {
  background-color: rgba(218, 54, 51, 0.2);
  color: #f85149;
}

.change-label.label-added {
  background-color: rgba(35, 134, 54, 0.2);
  color: #3fb950;
}

.change-label.label-status {
  background-color: rgba(187, 128, 9, 0.2);
  color: #d29922;
}

.change-label.label-priority {
  background-color: rgba(130, 80, 223, 0.2);
  color: #a371f7;
}

.change-label.label-audience {
  background-color: rgba(56, 139, 253, 0.2);
  color: #58a6ff;
}

.expand-icon {
  margin-left: auto;
  color: #8b949e;
  transition: transform 0.2s;
}

/* Entry changes */
.entry-changes {
  margin-left: 45px;
  padding-bottom: 1rem;
}

.change-item {
  background-color: #161b22;
  border: 1px solid #30363d;
  border-radius: 6px;
  margin-bottom: 0.5rem;
  overflow: hidden;
}

.change-header {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  gap: 0.5rem;
  transition: background-color 0.2s;
}

.change-header:hover {
  background-color: rgba(255, 255, 255, 0.03);
}

.change-type-badge {
  font-size: 10px;
  padding: 0.15rem 0.35rem;
  border-radius: 3px;
  text-transform: uppercase;
  font-weight: 600;
}

.badge-json {
  background-color: rgba(136, 192, 208, 0.2);
  color: #88c0d0;
}

.badge-string {
  background-color: rgba(163, 190, 140, 0.2);
  color: #a3be8c;
}

.badge-number {
  background-color: rgba(208, 135, 112, 0.2);
  color: #d08770;
}

.badge-boolean {
  background-color: rgba(180, 142, 173, 0.2);
  color: #b48ead;
}

.badge-addition {
  background-color: rgba(35, 134, 54, 0.3);
  color: #3fb950;
}

.badge-deletion {
  background-color: rgba(218, 54, 51, 0.3);
  color: #f85149;
}

.badge-default {
  background-color: rgba(139, 148, 158, 0.2);
  color: #8b949e;
}

.change-label-text {
  flex: 1;
  font-size: 13px;
  color: #c9d1d9;
}

.expand-icon-small {
  color: #8b949e;
  font-size: 11px;
}

.change-diff {
  border-top: 1px solid #30363d;
}

/* Footer */
.modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.footer-info {
  flex: 1;
}

/* Transitions */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
  max-height: 500px;
  overflow: hidden;
}

.slide-enter,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Close button */
.close {
  opacity: 0.8;
  font-size: 1.5rem;
}

.close:hover {
  opacity: 1;
}
</style>

